// useAttentionCounts: how many things are waiting for the operator, for the
// sidebar badges and the Home attention row. One hook so both always agree
// (and agree with the screens, which share the rules in ./predicates).
//
// Built only from the existing src/api hooks, called with the SAME
// arguments the screens use, so their query keys - and caches - are shared:
// with a screen open this adds no requests, and polling stays whatever that
// hook already does (useMeetings is limit=300 every 30 s; it is not made
// faster here).
//
//   meetings    useMeetings(c)                        ["meetings", c]
//   skills      usePendingSkills(c)                   ["skills", "pending", c]
//               useWorkshopProposals(c)               ["skill-workshop", c]
//   deploys     useCCDeploysList(c)                   ["cc-deploys", c, true]
//               (only where Apps is off; where it is on:)
//   apps        useAppsSummary(c)                     ["apps", c, "summary"]
//   todos       useCustomerSettings(c).todos_enabled  ["customer-settings", c]
//               (until it answers, the switch this browser remembered:
//               features/navigation/navFlags, so the count starts at once)
//               useTodos(c, "today", null)            ["todos", c, "today", null]
//   tasks       useTasksNow(c)                        ["tasks", c, "list", LIVE_TASKS_QUERY]
//               (tasks_enabled; counts.needs_you: an OK it waits for or an
//               answer not delivered; UX review H1)
//
// A family (child) login gets meetings only: the other endpoints are
// operator routes and would answer 403.

import { useMemo } from "react";

import { useAppsFlag, useAppsSummary } from "@/api/appsSummary";
import { useCCDeploysList } from "@/api/ccSessions";
import { useCustomerSettings } from "@/api/customerSettings";
import { useMeetings } from "@/api/meetings";
import { usePendingSkills } from "@/api/skills";
import { useWorkshopProposals } from "@/api/skillWorkshop";
import { useTodos } from "@/api/todos";
import { useWorkflowRuns, type RunSummary } from "@/api/workflows";
import { useRememberedNav } from "@/features/navigation/useNavFlags";
import { useTasksNow } from "@/features/tasks/useTasksNow";
import { jobsWaiting } from "@/features/workflows/jobs/jobModel";
import { todayKey } from "@/lib/format";
import { useAuth } from "@/stores/auth";
import {
  appsBadgeSource,
  type AppsCounts,
  countDeployments,
  countMeetingAttention,
  countPendingSkills,
  countTodosDue,
  type DeploymentCounts,
  type MeetingAttention,
} from "./predicates";

export interface AttentionCounts {
  meetings: MeetingAttention;
  /** Tier 2 review queue + unpromoted workshop proposals. */
  skills: { pending: number };
  /** Apps whose latest deploy has not passed its check (failing +
   *  unverified), of all apps; deployAttention() words and colours them. */
  deployments: DeploymentCounts;
  /** Where Apps is on: what waits on the operator (apps/summary); null where it is off. */
  apps: AppsCounts | null;
  /** Open todos due today and before today (local dates). All 0 when the
   *  workspace has Todos switched off (`enabled` false). */
  todos: { dueToday: number; overdue: number; enabled: boolean };
  /** Tasks waiting for the operator (an OK, or an answer not delivered),
   *  and of those the OKs. All 0 when Tasks are switched off. */
  tasks: { needsYou: number; awaitingOk: number; enabled: boolean };
  /** Team tasks whose plan waits for the operator's OK, oldest first. */
  jobs: { waiting: RunSummary[] };
  /** True while any count is still on its first load. */
  isLoading: boolean;
  /** True when any underlying query failed and has nothing to show, so a
   *  zero may be a failed load rather than nothing waiting. */
  isError: boolean;
  /** Fetch every underlying query again. */
  refetch: () => void;
}

const BADGE_POLL = { refetchInterval: 60_000 };

export function useAttentionCounts(customerId: string | null | undefined): AttentionCounts {
  const c = customerId || null;
  const isChild = useAuth((s) => s.role) === "family";
  const op = isChild ? null : c;

  // Badge counts poll slowly; a screen that shows the same data mounts its
  // own observer with its faster interval, which then sets the pace.
  const meetingsQ = useMeetings(c, BADGE_POLL);
  const pendingQ = usePendingSkills(op, BADGE_POLL);
  // Each workshop fetch costs one gateway tool call per agent - poll rarely.
  const workshopQ = useWorkshopProposals(op, { refetchInterval: 120_000 });
  // Apps (apps spec §10.6): under the switch apps/summary, not the legacy list.
  const source = appsBadgeSource(useAppsFlag(op));
  const deploysQ = useCCDeploysList(source === "legacy" ? op : null, BADGE_POLL);
  const appsQ = useAppsSummary(source === "apps" ? op : null);
  const settingsQ = useCustomerSettings(op);
  // The live switch once it answers; the remembered one until then, so the
  // todos read starts with the first wave instead of after settings.
  const remembered = useRememberedNav(op)?.flags;
  const todosEnabled = settingsQ.data ? settingsQ.data.todos_enabled === true : remembered?.todos_enabled === true;
  const todosQ = useTodos(todosEnabled ? op : null, "today", null, BADGE_POLL);
  const tasks = useTasksNow(op, { poll: BADGE_POLL.refetchInterval });
  // the same live list (and request) Working now reads
  const workflowsOn = settingsQ.data ? settingsQ.data.workflows_enabled === true : remembered?.workflows_enabled === true;
  const liveRunsQ = useWorkflowRuns(workflowsOn ? op : null, { live: true, poll: BADGE_POLL.refetchInterval });
  const waitingJobs = useMemo(() => jobsWaiting(liveRunsQ.data?.runs), [liveRunsQ.data]);

  // Recount when the day turns over, not only when the data changes:
  // "recent" and "overdue" depend on today's date.
  const today = todayKey();
  const meetings = useMemo(
    () => countMeetingAttention(meetingsQ.data?.meetings ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meetingsQ.data, today],
  );
  const todos = useMemo(
    () => countTodosDue(todosEnabled ? todosQ.data?.todos ?? [] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [todosQ.data, todosEnabled, today],
  );
  const deployments = useMemo(
    () => countDeployments(deploysQ.data?.deploys ?? []),
    [deploysQ.data],
  );
  const skillsPending = countPendingSkills(
    pendingQ.data?.skills ?? [],
    workshopQ.data?.proposals ?? [],
  );

  const queries = [meetingsQ, pendingQ, workshopQ, deploysQ, appsQ, settingsQ, todosQ];
  const isError = queries.some((q) => q.isError && q.data === undefined) || tasks.isError;
  // A switched-off query (a child login, Todos off) stays off: refetch()
  // would otherwise fetch it with a null customer.
  const refetch = () => {
    for (const q of queries) if (q.isEnabled) void q.refetch();
    tasks.refetch();
  };

  return {
    meetings,
    skills: { pending: skillsPending },
    deployments,
    apps: source === "apps" ? (appsQ.data ?? { down: 0, waiting_yes: 0, asking: 0 }) : null,
    todos: { ...todos, enabled: todosEnabled },
    tasks: { needsYou: tasks.enabled ? tasks.needsYou : 0, awaitingOk: tasks.enabled ? tasks.awaiting.length : 0, enabled: tasks.enabled },
    jobs: { waiting: waitingJobs },
    isLoading:
      meetingsQ.isLoading ||
      pendingQ.isLoading ||
      workshopQ.isLoading ||
      deploysQ.isLoading ||
      appsQ.isLoading ||
      settingsQ.isLoading ||
      todosQ.isLoading ||
      tasks.isLoading,
    isError,
    refetch,
  };
}
